import { evaluateRouteAccess, evaluateScopedRouteAccess, type RecordScopeStatus } from "../domain/capabilities";
import { resolveRecordScope } from "../domain/record-scope";
import { normalizeCoachingRole, type CoachingRole } from "../domain/roles";
import { isEvidenceModeEnabled, isLiveDataMode } from "../lib/config";

// Re-exported so route screens keep a single import surface for mode helpers.
export { isLiveMode } from "../lib/config";

export function getRouteDecision(
  role: CoachingRole,
  routePath: string,
  search: string,
  routeDecision: ReturnType<typeof evaluateRouteAccess>,
  liveScopeDecision?: Parameters<typeof evaluateScopedRouteAccess>[2]
) {
  if (!routeDecision.allowed || !routeDecision.route?.scopeKind || routeDecision.route.scopeKind === "none") {
    return routeDecision;
  }

  return evaluateScopedRouteAccess(
    role,
    routePath,
    getRecordScopeDecision(role, routeDecision.route, routePath, search, liveScopeDecision)
  );
}

function getRecordScopeDecision(
  role: CoachingRole,
  route: NonNullable<ReturnType<typeof evaluateRouteAccess>["route"]>,
  routePath: string,
  search: string,
  liveScopeDecision?: Parameters<typeof evaluateScopedRouteAccess>[2]
) {
  const evidenceScopeStatus = getEvidenceScopeStatus(search);
  if (evidenceScopeStatus) {
    return {
      status: evidenceScopeStatus,
      reason: getEvidenceScopeReason(route.scopeKind ?? "record", search)
    };
  }

  if (shouldUseLiveRecordScope() && liveScopeDecision) return liveScopeDecision;
  if (shouldUseLiveRecordScope()) {
    return {
      status: "unknown" as const,
      reason: "Record scope is resolving through the live API; no record data is rendered yet."
    };
  }

  return resolveRecordScope(role, route, routePath);
}

export function shouldResolveLiveRecordScope(decision: ReturnType<typeof evaluateRouteAccess>, search: string) {
  // TODO(release-summary): add a release-summary command that fails when any P0
  // live-proof ledger item is still in_progress/blocked, so a green mock e2e run
  // cannot be mistaken for live API/persistence proof (audit 2026-06-12 medium).
  return Boolean(
    shouldUseLiveRecordScope() &&
      decision.allowed &&
      decision.route?.scopeKind &&
      decision.route.scopeKind !== "none" &&
      !getEvidenceScopeStatus(search)
  );
}

function shouldUseLiveRecordScope() {
  return isLiveDataMode();
}

function getEvidenceScopeStatus(search: string): RecordScopeStatus | null {
  // URL `?scope=` is an evidence-only override: never trust it in live/prod.
  if (!isEvidenceModeEnabled()) return null;
  const scope = new URLSearchParams(search).get("scope");
  if (scope === "in") return "in-scope";
  if (scope === "out") return "out-of-scope";
  if (scope === "unknown") return "unknown";
  return null;
}

function getEvidenceScopeReason(scopeKind: string, search: string) {
  const status = getEvidenceScopeStatus(search);
  if (status === "in-scope") return undefined;
  if (status === "out-of-scope") return `This ${scopeKind} record is outside the active role scope.`;
  return `Record scope for this ${scopeKind} route has not been resolved; do not render record data.`;
}

export function getEvidenceRoleOverride(search: string): CoachingRole | null {
  // URL `?role=` is an evidence-only presentation override (Playwright/role
  // packs, manual capture in mock). It MUST be ignored whenever a live lane is
  // active so a production URL can never pick its own role; isEvidenceModeEnabled
  // already forces this off when authMode/dataMode is live.
  if (!isEvidenceModeEnabled()) return null;
  return normalizeEvidenceRole(new URLSearchParams(search).get("role"));
}

function normalizeEvidenceRole(value: string | null): CoachingRole | null {
  if (!value) return null;
  const role = normalizeCoachingRole(value);
  return role === value ? role : null;
}

export function normalizeProfileRole(value: unknown): CoachingRole | null {
  // The live /auth/me advisory roleHint vocabulary includes "member" (weakest
  // membership grant, e.g. the coaching-member bundle); it maps to the
  // read-only participant experience. Without this mapping a valid member
  // session was bounced to /login because no frontend role matched.
  if (value === "member") return "participant";
  const role = normalizeCoachingRole(value);
  return role === value ? role : null;
}
